import { Spinner, StatusBadge, Tooltip, TooltipTrigger } from "@components";
import { Button as PageButton } from "@components/button/button-untitledui";
import { dayjs } from "@lib";
import { ChevronDown, ClockRewind, File05 } from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { VEHICLE_STATUS_BADGE_PALETTES_BY_ID } from "@/features/vehicle/constants/vehicle-status.constant";
import {
	VEHICLE_STATUS_OPTIONS,
	VehicleStatus,
} from "@/features/vehicle/constants/vehicleStatus.enum";
import { editVehicleSituationLabel } from "@/features/vehicle/handlers/edit-vehicle-situation.handler";
import { mapBodyTypeToLabel } from "@/features/vehicle/handlers/mapEnumToLabel.handler";
import { useUpdateVehicleStatus } from "@/features/vehicle/query";
import { cx } from "@/utils/cx";
import { useVehicleDocumentUrl } from "../hooks";
import type { EditVehicleSituation } from "../types";

type DetailsHeaderProps = {
	vehicleId: string | number;
	brand: string;
	model: string;
	plate: string;
	chassis: string;
	odometer: number | null;
	odometerLastUpdatedAt?: string | null;
	brandValue: string;
	status?: EditVehicleSituation;
	className?: string;
	crlvDocumentId?: number;
	bodyType?: number | null;
};

export const DetailsHeader = ({
	vehicleId,
	brand,
	model,
	plate,
	chassis,
	odometer,
	odometerLastUpdatedAt,
	status = VehicleStatus.Disponivel,
	crlvDocumentId,
	bodyType,
	className,
}: DetailsHeaderProps) => {
	const updateStatusMutation = useUpdateVehicleStatus();
	const { mutate: getVehicleDocumentUrl, isPending: isGettingDocumentUrl } =
		useVehicleDocumentUrl();

	const handleStatusChange = (newStatus: VehicleStatus) => {
		if (newStatus === status) {
			return;
		}
		updateStatusMutation.mutate({
			id: String(vehicleId),
			status: newStatus,
		});
	};

	return (
		<div
			className={cx(
				"flex flex-col gap-y-2 rounded-[20px] border border-secondary_alt border-solid bg-bg-secondary p-6",
				className
			)}
		>
			<div className="flex flex-wrap items-center gap-3">
				<h1 className="font-semibold text-2xl text-primary leading-8">
					{brand || "-"} - {model || "-"}
				</h1>
				<Dropdown.Root>
					<AriaButton
						className="cursor-pointer rounded-full outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed"
						isDisabled={updateStatusMutation.isPending}
					>
						<StatusBadge
							label={
								<span className="inline-flex items-center gap-1">
									{updateStatusMutation.isPending ? <Spinner /> : null}
									{editVehicleSituationLabel(status)}
									<ChevronDown aria-hidden className="size-3.5" />
								</span>
							}
							palette={VEHICLE_STATUS_BADGE_PALETTES_BY_ID[status]}
							showDot={!updateStatusMutation.isPending}
						/>
					</AriaButton>

					<Dropdown.Popover>
						<Dropdown.Menu
							onAction={(selectedKey) => {
								const value = Number(selectedKey) as VehicleStatus;
								if (Object.values(VehicleStatus).includes(value)) {
									handleStatusChange(value);
								}
							}}
						>
							{VEHICLE_STATUS_OPTIONS.map((option) => (
								<Dropdown.Item
									id={String(option.value)}
									key={option.value}
									label={option.label}
								/>
							))}
						</Dropdown.Menu>
					</Dropdown.Popover>
				</Dropdown.Root>
			</div>

			<div className="grid grid-cols-[repeat(auto-fit,minmax(196px,1fr))] items-center justify-between gap-4">
				<div className="flex flex-col items-start">
					<span className="font-normal text-secondary text-sm leading-5">
						Placa
					</span>
					<span className="font-bold text-fg-primary text-md leading-6">
						{plate || "Não informado"}
					</span>
				</div>
				<div className="flex flex-col items-start">
					<span className="font-normal text-secondary text-sm leading-5">
						Chassi
					</span>
					<span className="font-bold text-fg-primary text-md leading-6">
						{chassis || "Não informado"}
					</span>
				</div>
				<div className="flex flex-col items-start">
					<span className="flex gap-1 font-normal text-secondary text-sm leading-5">
						Hodômetro
						<Tooltip
							arrow
							placement="right"
							title={
								odometerLastUpdatedAt
									? dayjs(odometerLastUpdatedAt).format(
											"DD/MM/YYYY [às] HH:mm:ss"
										)
									: "Não informado"
							}
						>
							<TooltipTrigger
								aria-label="Última atualização do hodômetro"
								className="inline-flex min-h-6 min-w-6 items-center justify-center rounded outline-focus-ring focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2"
							>
								<ClockRewind aria-hidden className="size-4 text-brand-500" />
							</TooltipTrigger>
						</Tooltip>
					</span>
					<span className="font-bold text-fg-primary text-md leading-6">
						{odometer !== null
							? `${odometer.toLocaleString("pt-BR")} km`
							: "Não informado"}
					</span>
				</div>
				{!!bodyType && (
					<div className="flex flex-col items-start">
						<span className="font-normal text-secondary text-sm leading-5">
							Tipo
						</span>
						<span className="font-bold text-fg-primary text-md leading-6">
							{mapBodyTypeToLabel(bodyType)}
						</span>
					</div>
				)}
				{!!crlvDocumentId && vehicleId && (
					<div className="flex flex-col items-start">
						<span className="font-normal text-secondary text-sm leading-5">
							CRLV
						</span>
						<PageButton
							className="py-0.5"
							color="link-color"
							iconTrailing={File05}
							isDisabled={isGettingDocumentUrl}
							onClick={() => {
								getVehicleDocumentUrl({
									vehicleId: String(vehicleId),
									documentId: String(crlvDocumentId),
									openUrl: true,
								});
							}}
							size="sm"
						>
							Ver documento
						</PageButton>
					</div>
				)}
			</div>
		</div>
	);
};
